import * as React from "react";
import { Check, Search, ChevronDown, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import { clientSelector } from "@/redux/clients/clients.selector";
import { CompaniesSearch } from "@/redux/clients/types/Clients.interface";
import { Input } from "./ui/input";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useEffect } from "react";
import { getCompaniesSearch } from "@/redux/clients/clients.action";
import { useNavigate } from "react-router-dom";
import { RoutesEnum } from "@/routes/enum/Routes";

type CarpetaDua = {
  numeroCarpeta: string;
  nroDua: string;
};

type Client = {
  id: number;
  nombre: string;
  numeroCliente: string;
  carpetasDua: CarpetaDua[];
};

export const SearchInput = () => {
  const { companiesSearch } = useSelector(clientSelector).ui;
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedValue, setSelectedValue] = React.useState("");
  const [expandedClients, setExpandedClients] = React.useState<
    Record<number, boolean>
  >({});
  const [showResults, setShowResults] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getCompaniesSearch());
  }, [dispatch]);
  // Función para filtrar clientes y sus datos
  const getFilteredClients = (query: string): CompaniesSearch[] => {
    // Si no hay query, devolver todos los clientes
    if (!query.trim()) return companiesSearch || [];

    return companiesSearch
      ?.map((client) => {
        const lowerQuery = query.toLowerCase();

        // Filtrar carpetas/DUAs que coincidan
        const filteredCarpetasDua = client.carpetasDua.filter(
          (item) =>
            item.numeroCarpeta.toLowerCase().includes(lowerQuery) ||
            item.nroDua.toLowerCase().includes(lowerQuery),
        );

        // Si el nombre del cliente o número de cliente coincide, devolver todo
        if (
          client.nombre.toLowerCase().includes(lowerQuery) ||
          client.numeroCliente.toLowerCase().includes(lowerQuery)
        ) {
          return client;
        }

        // Si alguna carpeta o DUA coincide, devolver el cliente con los datos filtrados
        if (filteredCarpetasDua.length > 0) {
          return {
            ...client,
            carpetasDua: filteredCarpetasDua,
          };
        }

        return null;
      })
      .filter(Boolean) as Client[];
  };

  const filteredClients = getFilteredClients(searchQuery);

  const toggleClientExpansion = (clientId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedClients((prev) => ({
      ...prev,
      [clientId]: !prev[clientId],
    }));
  };

  const handleSelectClient = (client: Client) => {
    setSelectedValue(`client-${client.id}`);
    setSearchQuery(`${client.nombre} (${client.numeroCliente})`);
    setShowResults(false);
    navigate(RoutesEnum.FOLDERS, {
      state: { clienteId: client.id, clienteNombre: client.nombre },
    });
  };

  const handleSelectCarpetaDua = (client: Client, carpetaDua: CarpetaDua) => {
    setSelectedValue(`carpeta-${carpetaDua.numeroCarpeta}`);
    setSearchQuery(
      `${client.nombre} - Carpeta ${carpetaDua.numeroCarpeta} / DUA ${carpetaDua.nroDua}`,
    );
    setShowResults(false);
    navigate(RoutesEnum.DATA_FOLDERS, {
      state: {
        folderId: carpetaDua.numeroCarpeta,
        nroDua: carpetaDua.nroDua,
        clienteNombre: client.nombre,
      },
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowResults(true); // Siempre mostrar resultados cuando hay cambios
    setSelectedValue("");
  };

  const handleInputFocus = () => {
    setShowResults(true); // Mostrar todos los elementos al hacer focus
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSelectedValue("");
    setShowResults(false);
    setExpandedClients({});
    inputRef.current?.focus();
  };

  return (
    <div className="w-full max-w-xl mx-auto min-w-full">
      <div className="space-y-4">
        <div className="relative">
          {/* Input de búsqueda directo */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#1F6B45]" />
            <Input
              ref={inputRef}
              type="text"
              className="w-full pl-10 pr-10 py-3 border border-input rounded-md bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              placeholder="Buscar"
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            )}
          </div>

          {/* Dropdown de resultados */}
          {showResults && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-sidebar shadow-lg">
              <div className="max-h-[400px] overflow-y-auto p-1">
                {filteredClients.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No se encontraron resultados.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredClients.map((client) => (
                      <div key={client.id} className="rounded-sm">
                        {/* Cliente principal */}
                        <div
                          className="flex items-center justify-between px-2 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-sm"
                          onClick={() => handleSelectClient(client)}
                        >
                          <div className="flex items-center min-w-0 flex-1">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">
                                {client.nombre}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Cliente: {client.numeroCliente} •{" "}
                                {client.carpetasDua.length} carpetas
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 flex-shrink-0",
                                selectedValue === `client-${client.id}`
                                  ? "opacity-100 text-primary"
                                  : "opacity-0",
                              )}
                            />
                            <div
                              onClick={(e) =>
                                toggleClientExpansion(client.id, e)
                              }
                              className="p-1 hover:bg-muted rounded-sm"
                            >
                              {expandedClients[client.id] ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Submenu de Carpetas/DUAs */}
                        {expandedClients[client.id] && (
                          <div className="ml-7 mt-1 mb-2 border-l-2 pl-2 border-muted">
                            {/* Contenedor con altura limitada y scroll */}
                            <div className="max-h-[120px] overflow-y-auto pr-1 space-y-1">
                              {client.carpetasDua.map((carpetaDua, index) => (
                                <div
                                  key={`${carpetaDua.numeroCarpeta}-${index}`}
                                  className="flex items-center justify-between px-2 py-1.5 cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-sm"
                                  onClick={() =>
                                    handleSelectCarpetaDua(client, carpetaDua)
                                  }
                                >
                                  <div className="flex items-center min-w-0 flex-1">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium">
                                        Carpeta {carpetaDua.numeroCarpeta}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        DUA: {carpetaDua.nroDua}
                                      </div>
                                    </div>
                                  </div>
                                  <Check
                                    className={cn(
                                      "h-4 w-4 flex-shrink-0",
                                      selectedValue ===
                                        `carpeta-${carpetaDua.numeroCarpeta}`
                                        ? "opacity-100 text-primary"
                                        : "opacity-0",
                                    )}
                                  />
                                </div>
                              ))}
                            </div>

                            {/* Indicador de scroll si hay más de 3 carpetas */}
                            {client.carpetasDua.length > 3 && (
                              <div className="text-xs text-muted-foreground mt-1 text-right pr-2">
                                {client.carpetasDua.length} carpetas • Scroll
                                para ver más
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay para cerrar el dropdown */}
      {showResults && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowResults(false)}
        />
      )}
    </div>
  );
};
